import { logger } from '@/sentry.config'
import { openBlankPdfWindow, renderPdfBytesInWindow } from '@/utils/pdfBytesWindow'
import { PDF_WASM_URL, pdfjsLib } from '@/utils/pdfjs'
import { Button } from '@helix-ds/react'
import * as Sentry from '@sentry/react'
import type { Span } from '@sentry/react'
import { ArrowUpRightFromSquare, Printer, ZoomIn, ZoomOut } from 'flowbite-react-icons/outline'
import React, { useEffect, useRef, useState } from 'react'

interface PDFViewerProps {
  pdfBytes: Uint8Array
  className?: string
  filename?: string
  /**
   * When ``false`` the control toolbar (zoom / Print / Open in New Window) is
   * hidden.
   *
   * Callers that embed the viewer inside their own chrome and provide their own
   * actions (e.g. the shipping-label modal, which has its own Print/Download)
   * pass ``false``. Defaults to ``true`` to preserve the standalone TRF
   * viewer's full toolbar.
   */
  showToolbar?: boolean
  /**
   * Called once the PDF has loaded, with the total number of pages. Callers
   * that render their own control row (e.g. the Ready-to-Send page) use this
   * to display the page count alongside their heading and action buttons.
   */
  onTotalPagesChange?: (totalPages: number) => void
}

const PDFViewer: React.FC<PDFViewerProps> = ({
  pdfBytes,
  className = '',
  filename = 'document.pdf',
  showToolbar = true,
  onTotalPagesChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [totalPages, setTotalPages] = useState(0)
  const [scale, setScale] = useState(1.0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [renderedPages, setRenderedPages] = useState<HTMLCanvasElement[]>([])
  // Toolbar actions report through this banner rather than the `error` state,
  // which would tear down the inline preview the user is reading.
  const [notice, setNotice] = useState<string | null>(null)
  const pdfDocRef = useRef<pdfjsLib.PDFDocumentProxy | null>(null)

  // The caller's `className` owns the viewer's height only when it passes a
  // real `height` utility (`h-*`). Emitting a hardcoded `h-screen` alongside it
  // would produce two conflicting `height` rules whose winner depends on CSS
  // source order, not intent — which made the viewer render near-viewport-tall
  // inside a bounded modal frame (e.g. h-[calc(100vh-360px)]), causing nested
  // scroll and empty gray space.
  //
  // `min-h-*` / `max-h-*` set min/max-height, NOT height, so they never
  // conflict with `h-screen` — and the viewer's `flex flex-col` + inner
  // `flex-1 overflow-auto` still needs a definite height to scroll. Callers
  // that pass only min/max height (e.g. the report viewer) must therefore KEEP
  // the `h-screen` fallback, or their page scroller breaks and the layout
  // inflates/clips. So match `h-*` while explicitly excluding `min-h-`/`max-h-`.
  const callerSetsHeight = /(^|\s)h-/.test(className)
  const rootHeightClass = callerSetsHeight ? '' : 'h-screen'

  useEffect(() => {
    // `cancelled` guards against this effect's async work landing after a
    // newer `pdfBytes` superseded it (or the component unmounted): without
    // it, a slow load that resolves after the cleanup below has already
    // destroyed its loading task would still overwrite `pdfDocRef` and state
    // with a doc whose worker is gone.
    let cancelled = false
    // Captured by the cleanup closure below rather than a ref: each effect
    // run gets its own `loadingTask` binding, so there is no risk of the
    // cleanup destroying a different run's task if effects overlap.
    let loadingTask: ReturnType<typeof pdfjsLib.getDocument> | null = null

    const loadPDF = async () => {
      try {
        setLoading(true)
        setError(null)

        // Create a copy of the ArrayBuffer to avoid detachment issues
        const bytesCopy = pdfBytes.slice(0)

        // Load the PDF document from bytes. `wasmUrl` is required since v6:
        // without it the JBIG2/CCITT-fax decoders silently fail and those
        // images render as black boxes instead of erroring.
        loadingTask = pdfjsLib.getDocument({ data: bytesCopy, wasmUrl: PDF_WASM_URL })
        const pdfDoc = await loadingTask.promise
        if (cancelled) return
        pdfDocRef.current = pdfDoc

        setTotalPages(pdfDoc.numPages)
        setLoading(false)
      } catch (err) {
        if (cancelled) return
        logger.error('PDF loading failed', {
          error: err instanceof Error ? err.message : String(err),
        })
        setError(err instanceof Error ? err.message : 'Failed to load PDF')
        setLoading(false)
      }
    }

    if (pdfBytes) {
      loadPDF()
    }

    // v6 removed `PDFDocumentProxy.destroy()`; the worker transport can only
    // be released through the *loading task's* `destroy()`. Without this,
    // every `pdfBytes` change or unmount leaked a pdfjs document and its
    // worker for the rest of the session.
    return () => {
      cancelled = true
      pdfDocRef.current = null
      if (loadingTask) {
        void loadingTask.destroy().catch((error: unknown) => {
          logger.error('PDF document cleanup failed', {
            error: error instanceof Error ? error.message : String(error),
          })
        })
      }
    }
  }, [pdfBytes])

  useEffect(() => {
    if (totalPages > 0) {
      onTotalPagesChange?.(totalPages)
    }
  }, [totalPages, onTotalPagesChange])

  useEffect(() => {
    const renderAllPages = async () => {
      if (!pdfDocRef.current || totalPages === 0) {
        return
      }

      try {
        const canvases: HTMLCanvasElement[] = []

        for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
          const page = await pdfDocRef.current.getPage(pageNum)
          const canvas = document.createElement('canvas')
          const context = canvas.getContext('2d')

          const viewport = page.getViewport({ scale })
          canvas.height = viewport.height
          canvas.width = viewport.width

          const renderContext = {
            canvasContext: context!,
            viewport: viewport,
            canvas: canvas,
          }

          await page.render(renderContext).promise
          canvases.push(canvas)
        }

        setRenderedPages(canvases)
      } catch (err) {
        logger.error('PDF page rendering failed', {
          error: err instanceof Error ? err.message : String(err),
        })
        setError(err instanceof Error ? err.message : 'Failed to render pages')
      }
    }

    if (pdfDocRef.current && totalPages > 0) {
      renderAllPages()
    }
  }, [totalPages, scale])

  /**
   * Spawns a tab and paints the same bytes this viewer is showing into it as
   * canvases, with our own Download and Print controls.
   *
   * Epic's embedded browser cannot render a PDF document in a popup at all —
   * diagnostics confirmed its popups land on an opaque (``null``) origin and
   * that even a fully self-contained ``data:application/pdf`` iframe stays
   * blank, so there is no PDF renderer to hand a blob/data/served document to.
   * Painting pixels ourselves is the only thing that displays there. ``print``
   * asks the tab for the print dialog once its pages are painted.
   */
  const spawnPdfTab = (span: Span, operation: 'pdf_open' | 'pdf_print', print: boolean) => {
    span.setAttribute('pdf.filename', filename)
    span.setAttribute('pdf.size', pdfBytes.length)
    setNotice(null)

    // Opening has to happen in the click's own tick — anything deferred is
    // treated as an unsolicited popup and blocked.
    const spawned = openBlankPdfWindow()

    if (!spawned) {
      span.setAttribute('error', true)
      span.setAttribute('error.message', 'Popup blocked')
      Sentry.captureException(new Error('Popup blocked. Please allow popups for this site.'), {
        tags: { operation: `ui.action.${operation}` },
      })
      setNotice('Popups are blocked for this site. Allow them to open the requisition in a new tab.')
      return
    }

    renderPdfBytesInWindow(spawned, pdfBytes, { filename, print }).catch((err: unknown) => {
      span.setAttribute('error', true)
      if (err instanceof Error) {
        span.setAttribute('error.message', err.message)
      }
      logger.error('PDF rendering in new window failed', {
        error: err instanceof Error ? err.message : String(err),
      })
      Sentry.captureException(err, { tags: { operation: `ui.action.${operation}` } })
      setNotice('The PDF could not be rendered in the new tab.')
    })
  }

  const openPDFInNewWindow = () => {
    return Sentry.startSpan({ op: 'ui.click', name: 'Open PDF in New Window' }, (span) =>
      spawnPdfTab(span, 'pdf_open', false)
    )
  }

  /**
   * Prints from a spawned tab showing the PDF in the browser's native viewer,
   * so the output is vector, sized for paper, and free of the host page chrome.
   */
  const printPDF = () => {
    return Sentry.startSpan({ op: 'ui.click', name: 'Print PDF' }, (span) => spawnPdfTab(span, 'pdf_print', true))
  }

  const zoomIn = () => {
    setScale((prev) => Math.min(prev + 0.25, 3.0))
  }

  const zoomOut = () => {
    setScale((prev) => Math.max(prev - 0.25, 0.5))
  }

  if (loading) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-brand-600 border-r-transparent dark:border-brand-500"></div>
          <p className="mt-2 text-sm text-text-body dark:text-gray-400">Loading PDF...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className="text-center">
          <p className="mb-4 text-text-fg-error dark:text-error-100">Error: {error}</p>
          <Button variant="secondary" size="md" onClick={() => setError(null)} className="focus-visible:ring-brand-600">
            Try Again
          </Button>
        </div>
      </div>
    )
  }

  // The inline canvas preview is the only view this component renders. Toolbar
  // actions paint their own tab from the same bytes, so the preview the user is
  // reading is never swapped out for a placeholder.
  return (
    <div className={`flex flex-col ${rootHeightClass} ${className}`.trim()}>
      {/* PDF Controls */}
      {showToolbar && (
          <div className="flex flex-shrink-0 items-center justify-between border-b bg-background-tertiary p-4 dark:bg-gray-800">
          <div className="flex items-center space-x-4">
            <span className="text-sm text-text-body dark:text-gray-400">
              {totalPages} page{totalPages !== 1 ? 's' : ''}
            </span>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={zoomOut}
              className="rounded-xs border border-border-base-medium bg-white px-3 py-1 hover:bg-background-secondary-medium dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800"
              title="Zoom Out"
              aria-label="Zoom out"
            >
              <ZoomOut className="h-5 w-5" />
            </button>
            <span className="text-sm text-text-body dark:text-gray-400">{Math.round(scale * 100)}%</span>
            <button
              onClick={zoomIn}
              className="rounded-xs border border-border-base-medium bg-white px-3 py-1 hover:bg-background-secondary-medium dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800"
              title="Zoom In"
              aria-label="Zoom in"
            >
              <ZoomIn className="h-5 w-5" />
            </button>
            <button
              onClick={printPDF}
              className="rounded-xs border border-border-base-medium bg-white px-3 py-1 hover:bg-background-secondary-medium dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800"
              title="Print"
              aria-label="Print"
            >
              <Printer className="h-5 w-5" />
            </button>
            <button
              onClick={openPDFInNewWindow}
              className="rounded-xs border border-brand-600 bg-brand-600 px-3 py-1 text-white hover:bg-brand-700 dark:border-brand-500"
              title="Open in New Window"
              aria-label="Open in New Window"
            >
              <ArrowUpRightFromSquare className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {notice && (
        <div
          role="alert"
          className="flex flex-shrink-0 items-center justify-between gap-4 border-b border-warning-300 bg-warning-50 px-4 py-2 text-sm text-text-fg-warning dark:border-warning-600 dark:bg-gray-900 dark:text-warning-100"
        >
          <span>{notice}</span>
          <button onClick={() => setNotice(null)} className="font-medium underline" title="Dismiss">
            Dismiss
          </button>
        </div>
      )}

      {/* Scrollable PDF Container */}
      <div ref={containerRef} className="min-h-0 flex-1 overflow-auto bg-background-quaternary p-4 dark:bg-gray-800">
        {/*
          `min-w-max` sizes this track to the widest page, so `items-center`
          only ever centers pages *within* it. Without it, a zoomed page
          (>~125%) is wider than the scroll container and `items-center`
          splits the overflow across both edges — and overflow past a scroll
          container's start edge is unreachable, permanently clipping the
          left of the page. Pages still center whenever they fit.
        */}
        <div className="flex min-w-max flex-col items-center space-y-4">
          {renderedPages.map((canvas, index) => (
            <div key={index} className="bg-white shadow-lg dark:bg-gray-900">
              <canvas
                ref={(el) => {
                  if (el) {
                    // Clone the canvas to avoid issues with multiple renders
                    const context = el.getContext('2d')
                    if (context) {
                      el.width = canvas.width
                      el.height = canvas.height
                      context.drawImage(canvas, 0, 0)
                    }
                  }
                }}
                className="block"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default PDFViewer
